import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Calculator, ClipboardCheck, Tags } from 'lucide-react';
import { useAuth } from '../../../services/authentication/authContext';
import { getSetupOverview, SetupOverviewResponse, SetupOverviewRestaurant, SetupType } from '../../../services/setupOverview/setupOverviewService';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { isAllRestaurantsSelected, useAppUserDefaultStore } from '../../../stores/appUserDefaultStore';
import { RestaurantViewScope, useScopedRestaurant } from '../../../hooks/restaurantViewScope';
import { cn } from '../../../lib/utils';
import { cardClass, greenButtonClass, greenTextColor, iconBgBlue, iconBgGreen, iconBgPurple, primaryText, secondaryText } from '../../../styling/stylingHelper';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import { MobileCardField, MobileCardList, ResponsiveTable } from '../../shared/mobile/ResponsiveTable';
import { StatCard } from '../../ui/StatCard';
import { TabbedSections } from '../../ui/TabbedSections';
import { TableComponent } from '../../ui/TableComponent';
import { SetupOverviewItemsTable } from './SetupOverviewItemsTable';
import { useSetupOverviewEditor } from './useSetupOverviewEditor';

export default function SetupOverviewPage() {
  const { backendUser } = useAuth();
  const navbarRestaurantId = useAppUserDefaultStore(state => state.restaurant?.id);
  // Remount on account change so previous account data can never flash during a reload.
  return <SetupOverview key={`${backendUser?.id}:${navbarRestaurantId}`} />;
}

function SetupOverview() {
  const navbarRestaurant = useAppUserDefaultStore(state => state.restaurant);
  const restaurants = useRestaurantStore(state => state.restaurants);
  const [detailId, setDetailId] = useState<string | null>(null);
  const canDrillDown = isAllRestaurantsSelected(navbarRestaurant);
  const restaurant = (canDrillDown && detailId ? restaurants.find(row => row.id === detailId) : undefined) ?? navbarRestaurant;
  return <RestaurantViewScope restaurant={restaurant} navbarRestaurantId={navbarRestaurant?.id}>
    <SetupOverviewContent canGoBack={canDrillDown && !!detailId} onBack={() => setDetailId(null)}
      onOpenRestaurant={id => { if (canDrillDown) setDetailId(id); }} />
  </RestaurantViewScope>;
}

function SetupOverviewContent({ canGoBack, onBack, onOpenRestaurant }: {
  canGoBack: boolean; onBack: () => void; onOpenRestaurant: (id: string) => void;
}) {
  const { t } = useTranslation();
  const selected = useScopedRestaurant();
  const [data, setData] = useState<SetupOverviewResponse | null>(null);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [type, setType] = useState<SetupType>('all');
  const [visibleCount, setVisibleCount] = useState(20);
  const refresh = useCallback(() => setReload(value => value + 1), []);
  const editor = useSetupOverviewEditor(refresh);

  useEffect(() => {
    const controller = new AbortController();
    setError(false);
    getSetupOverview(controller.signal).then(result => {
      if (!controller.signal.aborted) setData(result);
    }).catch(cause => {
      if (controller.signal.aborted) return;
      console.error('Failed to load setup overview', cause);
      setError(true);
    });
    return () => controller.abort();
  }, [reload]);

  const allRestaurants = isAllRestaurantsSelected(selected);
  const title = !allRestaurants && selected ? selected.name : t('setupOverview.title');
  const metric = allRestaurants ? data?.aggregate[type] : data?.restaurants.find(row => row.restaurantId === selected?.id)?.metrics[type];
  const percentage = (value: number | null | undefined, empty = 'setupOverview.noData') =>
    value == null ? t(empty) : `${value}%`;
  const costValue = (row: SetupOverviewRestaurant) => percentage(row.metrics[type].cost.percentage,
    row.metrics[type].category.total ? 'setupOverview.notApplicable' : 'setupOverview.noData');
  const openRestaurant = (row: SetupOverviewRestaurant) => {
    onOpenRestaurant(row.restaurantId);
  };
  const rows = data?.restaurants ?? [];
  const tableRows = rows.map(row => ({ ...row,
    overall: percentage(row.metrics[type].score),
    category: percentage(row.metrics[type].category.percentage),
    cost: costValue(row),
  }));

  return <div className={cn('p-3 min-[650px]:p-6', primaryText)}>
    <MobilePageHeader title={title} />
    <header className="mb-6 space-y-4">
      <div className="flex items-start gap-2">
        {canGoBack && <button type="button" onClick={onBack}
          aria-label={t('setupOverview.backToRestaurants')}
          className={cn('flex min-h-[44px] items-center gap-1.5', secondaryText)}>
          <ArrowLeft className="h-6 w-6 shrink-0" />
          <span className="lg:hidden">{t('setupOverview.backToRestaurants')}</span>
        </button>}
        <div>
          <h1 className="hidden text-2xl font-semibold lg:block">{title}</h1>
          <p className={cn('mt-1 text-sm', secondaryText)}>{t('setupOverview.period')}</p>
        </div>
      </div>
      <TabbedSections isTimeOptionSelection={false} maxTabsBeforeDropdown={4} value={type}
        options={(['all', 'food', 'drinks', 'other'] as const).map(value => ({ value, label: t(`overallType.${value}`) }))}
        onChange={value => setType(value as SetupType)} />
    </header>

    {error ? <div role="alert" className={cn(cardClass, 'p-5 space-y-3')}>
      <p>{t('setupOverview.error')}</p>
      <button type="button" className={greenButtonClass} onClick={() => setReload(value => value + 1)}>{t('setupOverview.retry')}</button>
    </div> : !data ? <p role="status" className={secondaryText}>{t('common.loading')}</p> : <>
      <div className="grid grid-cols-1 gap-3 sm:gap-6 lg:grid-cols-3">
        <StatCard icon={<ClipboardCheck className={greenTextColor} />} iconBg={iconBgGreen}
          title={t('setupOverview.overall')} value={percentage(metric?.score)} />
        <StatCard icon={<Tags className="text-blue-600 dark:text-blue-400" />} iconBg={iconBgBlue}
          title={t('setupOverview.categories')} value={percentage(metric?.category.percentage)}
          subtitle={t('setupOverview.categoryCount', { completed: metric?.category.completed ?? 0, total: metric?.category.total ?? 0 })} />
        <StatCard icon={<Calculator className="text-purple-600 dark:text-purple-400" />} iconBg={iconBgPurple}
          title={t('setupOverview.cost')} value={percentage(metric?.cost.percentage, metric?.category.total ? 'setupOverview.notApplicable' : 'setupOverview.noData')}
          subtitle={t('setupOverview.costCount', { completed: metric?.cost.completed ?? 0, total: metric?.cost.total ?? 0 })} />
      </div>
      {!metric?.category.total && <p className={cn('mt-5', secondaryText)}>{t('setupOverview.empty')}</p>}
      {editor.busy && <p role="status" className={cn('mt-3', secondaryText)}>{t('common.loading')}</p>}
      {selected && !allRestaurants && (editor.dataError ? <div role="alert" className="mt-6 space-y-3">
        <p>{t('setupOverview.pricingError')}</p>
        <button type="button" className={greenButtonClass} onClick={editor.retryPreload}>{t('setupOverview.retry')}</button>
      </div> : !editor.dataReady ? <p role="status" className={cn('mt-6', secondaryText)}>{t('common.loading')}</p> :
        <SetupOverviewItemsTable key={selected.id} items={data.items} type={type}
          restaurantId={selected.id} busy={editor.busy}
          onEditCategory={editor.openCategory} onEditCost={editor.openCost} />)}
      {allRestaurants && <section className="mt-6" aria-label={t('setupOverview.restaurants')}>
        <ResponsiveTable desktop={<TableComponent
          title={t('setupOverview.restaurants')} data={tableRows} usePagination blankLines={false} onRowClick={openRestaurant}
          columns={[
            { key: 'restaurantName', label: t('setupOverview.restaurant'), render: (_, row) =>
              <button type="button" className="min-h-[44px] text-left font-medium" onClick={event => { event.stopPropagation(); openRestaurant(row); }}>{row.restaurantName}</button> },
            { key: 'category', label: t('setupOverview.categories') },
            { key: 'cost', label: t('setupOverview.cost') },
            { key: 'overall', label: t('setupOverview.overall') },
          ]} />}
          mobile={<>
            <h2 className="mb-3 text-lg font-semibold">{t('setupOverview.restaurants')}</h2>
            <MobileCardList items={rows.slice(0, visibleCount)} keyOf={row => row.restaurantId}
              onLoadMore={() => setVisibleCount(value => value + 20)} hasMore={rows.length > visibleCount}
              loadMoreLabel={t('setupOverview.loadMore')} renderCard={row =>
              <div className={cn(cardClass, 'p-4 space-y-3')}>
                <button type="button" onClick={() => openRestaurant(row)} className={cn('min-h-[44px] text-left font-semibold underline underline-offset-4', greenTextColor)}>{row.restaurantName}</button>
                <div className="grid grid-cols-1 gap-3">
                  <MobileCardField label={t('setupOverview.overall')} value={percentage(row.metrics[type].score)} />
                  <MobileCardField label={t('setupOverview.categories')} value={percentage(row.metrics[type].category.percentage)} />
                  <MobileCardField label={t('setupOverview.cost')} value={costValue(row)} />
                </div>
              </div>} />
          </>} />
      </section>}
    </>}
    {editor.overlays}
  </div>;
}
